import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HardDrive, Server, RefreshCw, AlertTriangle } from 'lucide-react';
import { Container, PageHeader, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { CompactStat, CompactStatGroup } from '../components/CompactStat';
import { usePackagerCache, useFastChannelServices, type PackagerCache } from '@/api/fastchannel';
import { formatBytes } from '@/pages/services/_shared/analytics-format';

const BASE = '/services/fastchannel';

/** Pick a tone/colour for a disk-usage percentage (green → amber → red). */
function usageTone(pct: number): { tone: 'success' | 'warning' | 'danger'; color: string } {
  if (pct >= 90) return { tone: 'danger', color: 'var(--vn-danger-solid)' };
  if (pct >= 70) return { tone: 'warning', color: 'var(--vn-warning-solid)' };
  return { tone: 'success', color: 'var(--vn-success-solid)' };
}

/** A thin horizontal usage bar, coloured by fill level. */
function UsageBar({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct));
  const { color } = usageTone(clamped);
  return (
    <div
      style={{
        height: 6,
        borderRadius: 'var(--vn-radius-sm)',
        background: 'var(--vn-bg-muted)',
        overflow: 'hidden',
        minWidth: 90,
      }}
    >
      <div style={{ width: `${clamped}%`, height: '100%', background: color, transition: 'width 0.3s' }} />
    </div>
  );
}

export function CacheStatusPage() {
  const navigate = useNavigate();
  const { serviceUuid } = useParams<{ serviceUuid: string }>();

  const { data: services = [] } = useFastChannelServices();
  const service = services.find((s) => s.id === serviceUuid);
  const { data, isLoading, isError, refetch, isFetching } = usePackagerCache();

  const totals = data?.totals;
  const packagers = data?.packagers ?? [];
  const usedPct =
    totals && totals.disk_total_bytes > 0
      ? (totals.disk_used_bytes / totals.disk_total_bytes) * 100
      : 0;

  const columns = useMemo<ColumnDef<PackagerCache, unknown>[]>(
    () => [
      {
        id: 'id',
        header: 'Packager',
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Server size={14} color="var(--vn-fg-muted)" />
            <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.id}</span>
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.reachable ? (
            <StatusPill tone="success">Reachable</StatusPill>
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <StatusPill tone="danger">Unreachable</StatusPill>
              {row.original.error ? (
                <span title={row.original.error} style={{ color: 'var(--vn-fg-muted)', display: 'inline-flex' }}>
                  <AlertTriangle size={14} />
                </span>
              ) : null}
            </span>
          ),
      },
      {
        id: 'disk',
        header: 'Disk (PVC)',
        enableSorting: false,
        cell: ({ row }) => {
          const disk = row.original.stats?.disk;
          if (!disk) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
          const { tone } = usageTone(disk.used_pct);
          return (
            <div style={{ display: 'grid', gap: 4, minWidth: 150 }}>
              <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
                <span style={{ color: `var(--vn-${tone}-text)`, fontWeight: 'var(--vn-font-weight-medium)' }}>
                  {disk.used_pct.toFixed(1)}%
                </span>{' '}
                <span style={{ color: 'var(--vn-fg-muted)' }}>
                  ({formatBytes(disk.used_bytes)} / {formatBytes(disk.total_bytes)})
                </span>
              </span>
              <UsageBar pct={disk.used_pct} />
            </div>
          );
        },
      },
      {
        id: 'free',
        header: 'Free',
        enableSorting: false,
        cell: ({ row }) => {
          const disk = row.original.stats?.disk;
          return disk ? formatBytes(disk.free_bytes) : '—';
        },
      },
      {
        id: 'srccache',
        header: 'Source cache',
        enableSorting: false,
        cell: ({ row }) => {
          const sc = row.original.stats?.srccache;
          if (!sc) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
          return (
            <span>
              {formatBytes(sc.bytes)}{' '}
              <span style={{ color: 'var(--vn-fg-muted)' }}>({sc.files.toLocaleString()} files)</span>
            </span>
          );
        },
      },
      {
        id: 'channels',
        header: 'Channels',
        enableSorting: false,
        cell: ({ row }) => row.original.stats?.channels?.length ?? 0,
      },
    ],
    [],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<HardDrive size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href={BASE} onClick={(e) => { e.preventDefault(); navigate(BASE); }}>FastChannel</BreadcrumbItem>
            <BreadcrumbItem
              href={`${BASE}/${serviceUuid}`}
              onClick={(e) => { e.preventDefault(); navigate(`${BASE}/${serviceUuid}`); }}
            >
              {service?.service_name || 'Channels'}
            </BreadcrumbItem>
            <BreadcrumbItem current>Cache status</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Cache & PVC status"
        description="Disk usage of each packager's persistent cache. The source + transmux caches do not evict on their own — watch the headroom here."
        actions={
          <Button variant="secondary" startIcon={<RefreshCw size={15} />} disabled={isFetching} onClick={() => void refetch()}>
            Refresh
          </Button>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Summary — CompactStat in a flat Card, the console's detail-page KPI pattern. */}
      <Card flat>
        <CardBody>
          <CompactStatGroup columns={4}>
            <CompactStat
              label="Packagers"
              value={totals ? `${totals.reachable} / ${totals.packagers}` : '—'}
              hint="reachable / total"
            />
            <CompactStat
              label="Disk used"
              value={totals ? `${usedPct.toFixed(1)}%` : '—'}
              hint={totals ? `${formatBytes(totals.disk_used_bytes)} of ${formatBytes(totals.disk_total_bytes)}` : undefined}
            />
            <CompactStat label="Disk free" value={totals ? formatBytes(totals.disk_free_bytes) : '—'} />
            <CompactStat
              label="Source cache"
              value={totals ? formatBytes(totals.srccache_bytes) : '—'}
              hint={totals ? `${totals.srccache_files.toLocaleString()} files` : undefined}
            />
          </CompactStatGroup>
        </CardBody>
      </Card>

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 56 }} />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          icon={<AlertTriangle size={28} />}
          title="Could not load cache status"
          description="The control plane did not return packager stats. It may be unreachable or no packagers are registered."
          actions={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      ) : packagers.length === 0 ? (
        <EmptyState
          icon={<Server size={20} />}
          title="No packagers registered"
          description="No live packager workers are reporting to the control plane yet."
        />
      ) : (
        <DataTable
          columns={columns}
          data={packagers}
          getRowId={(r) => r.id}
          emptyState="No packagers reporting."
        />
      )}
      </div>
    </Container>
  );
}

export default CacheStatusPage;
